import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { DataLoadStatus } from "data/enums/dataLoadStatus";
import LinkButton from "features/shared/components/buttons/LinkButton";
import PageCard from "features/shared/components/card/PageCard";
import PromiseLoadingSpinner from "features/shared/components/loaders/PromiseLoadingSpinner";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import React from "react";
import { useTranslation } from "react-i18next";
import DashboardCardRow from "./DashboardCardRow";
import styles from "./dashboardListingCard.module.scss";

interface Props<T> {
  title: string;
  items: T[];
  renderItem: (item: T) => JSX.Element;
  itemsLoadStatus: DataLoadStatus;
  renderHeader?: () => JSX.Element;
  noItemsMessage?: string;
  moduleRoute?: string;
  icon?: IconProp;
  iconColor?: string;
  background?: string;
  count?: number;
  loaderArea?: string;
  onClickLinkButton?: () => void
}

const DashboardListingCard = <T extends object>({
  title,
  items,
  renderItem,
  itemsLoadStatus,
  noItemsMessage,
  moduleRoute,
  icon,
  iconColor,
  background,
  count = 4,
  loaderArea,
  onClickLinkButton,
  renderHeader
}: Props<T>) => {

  // tranlsations
  const tLabels = useTranslation("dashboard\\dashboard").t;

  // sub-renders
  const renderItems = () => {
    return (
      items.map((item, index) => (
        <DashboardCardRow key={index}>
          {renderItem(item)}
        </DashboardCardRow>
      ))
    );
  }
  
  const renderLinkButton = () => {
    if (items.length > count - 1 && moduleRoute) {
      return (
        <div className="text-center mt-2">          
          <LinkButton asLink to={moduleRoute} onClick={onClickLinkButton} />
        </div>
      );
    }
  };

  const renderSpinner = () => {
    if (loaderArea) {
      return <PromiseLoadingSpinner area={loaderArea} />;
    }
  };

  const renderNoContentInfo = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-muted font-italic">
        {noItemsMessage ?? tLabels("dashboardListingCard.noRecordsFound")}
      </div>
    );    
  }

  const renderItemsHeader = () => {
    if (renderHeader) {
      return renderHeader();
    }    
  }

  // render
  return (
    <PageCard
      title={title}
      icon={icon}
      iconColor={iconColor}
      background={background}
      cardClassName={styles.card}
      headerClassName={styles.cardHeader}
    >
      <div className="position-relative h-100">
        <NoContentWrapper 
          data={items}
          loadStatus={itemsLoadStatus}
          noContentRender={renderNoContentInfo}
        >
          {renderItemsHeader()}
          {renderItems()}
          {renderLinkButton()}          
        </NoContentWrapper>
        {renderSpinner()}
      </div>
    </PageCard>
  );
};

export default DashboardListingCard;
